import { useCallback, useEffect, useRef, useState } from 'react';
import { extractGraphQLError, handleApolloError } from 'utils/errorHandlers';
import { useGetDevicesByOrganizationQuery } from 'gqlHooks';
import type { EuiSelectableOption } from '@elastic/eui';
import type { ApolloError } from '@apollo/client';
import { noop } from 'lodash';

import { SelectDevicesView } from './devices-select-view';

export type SelectDevicesProps = {
  onChange: (event: any) => void;
  selectedDevice: string | string[] | undefined;
  organizationId: string;
  isInvalid?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  label?: string;
  isSingleSelect?: boolean;
  isRequired: boolean;
  onBlur?: (event: string | React.ChangeEvent<Element>) => void;
  inFlyout?: boolean;
  /** All devices that are tied to a group and to be displayed in the table*/
  assignedDevices?: any[];
};

export const SelectDevices = (props: SelectDevicesProps) => {
  const [errorState, setErrorState] = useState<string | null>(null);
  const [options, setOptions] = useState<EuiSelectableOption[]>([]);
  const [assignedDevices, setAssignedDevices] = useState<EuiSelectableOption[]>([]);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const assignedDevicesRef = useRef(assignedDevices);

  useEffect(() => {
    assignedDevicesRef.current = assignedDevices;
  }, [assignedDevices]);

  useEffect(() => {
    if (props.assignedDevices) {
      const mappedOptions: EuiSelectableOption[] = props.assignedDevices.map(device => ({
        label: device.label ? `${device.label} - ${device.identifier}` : `${device.identifier}`,
        key: `${device.identifier}`,
        value: device.identifier,
        checked: 'on' as const,
      }));
      setAssignedDevices(mappedOptions);
    }
  }, [props.assignedDevices]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleOnChange = (newOptions: EuiSelectableOption[]) => {
    setOptions(newOptions);
    props.onChange(newOptions);
  };

  const handleError = useCallback((error: ApolloError) => {
    const gqlError = extractGraphQLError(error);
    if (gqlError.statusCode === 404) {
      setErrorState(gqlError.errorMessage);
    } else {
      handleApolloError(error, true, gqlError => setErrorState(gqlError.errorMessage));
    }
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  }, []);

  const { loading } = useGetDevicesByOrganizationQuery({
    variables: {
      organizationId: props.organizationId,
      limit: 25,
      searchTerm: debouncedSearch || undefined,
    },
    skip: !props.organizationId,
    fetchPolicy: 'network-only',
    onError: handleError,
    onCompleted: data => {
      const ad = assignedDevicesRef.current;
      const assignedKeys = new Set(ad.map(d => d.key));
      const items = data?.devices?.items ?? [];
      const deviceOptions: EuiSelectableOption[] = items
        .filter(device => !assignedKeys.has(device.identifier ?? ''))
        .map(device => ({
          label: device.label ? `${device.label} - ${device.identifier}` : `${device.identifier}`,
          key: device.identifier ?? '',
          value: device.identifier,
          checked: undefined,
        }));
      setOptions([...ad, ...deviceOptions]);
    },
  });

  return (
    <SelectDevicesView
      inFlyout={props.inFlyout}
      onChange={handleOnChange}
      disabled={props.disabled}
      options={options}
      label={props.label}
      onBlur={props.onBlur ?? noop}
      isInvalid={errorState != null}
      errorMessage={errorState}
      isLoading={loading}
      isSingleSelect={props.isSingleSelect}
      isRequired={props.isRequired}
      onSearchChange={handleSearchChange}
    />
  );
};
